import React, { useContext, useEffect, useRef } from "react";
import { connectStateResults } from "react-instantsearch-dom";
import { cloneDeep, isEqual, keyBy, merge, union } from "lodash";

import { MatchContext, SearchContext } from "context/providers";
import { getFilteredUserIDs } from "utils/helpers/search";

function mergeHitsIfChangedById(oldHits, newHits) {
  const stripRank = (hit) => {
    const { rankDetails, ...rest } = hit || {};
    return rest;
  };

  const oldById = keyBy(oldHits, "id");
  const newById = keyBy(newHits, "id");

  const allIds = union(Object.keys(oldById), Object.keys(newById));

  return allIds
    .map((id) => {
      const oldHit = oldById[id] || {};
      const newHit = newById[id] || {};

      const oldClean = stripRank(oldHit);
      const newClean = stripRank(newHit);

      if (!isEqual(oldClean, newClean)) {
        return merge({}, oldHit, newHit);
      }

      return oldHit;
    })
    .filter((hit) => Object.keys(hit).length > 0);
}

const buildTKSkillsBasedOnHierarchy = (hit) => {
  const clonedHit = cloneDeep(hit);

  if (clonedHit.TKSkillsHierarchy) {
    const skillMap = new Map();

    clonedHit.TKSkillsHierarchy.forEach((entry) => {
      const parts = entry.split(" > ");
      if (parts.length >= 2) {
        const key = parts.slice(0, 2).join(" > ");
        const obj = {
          category: parts[0],
          name: parts[1],
        };
        if (parts.length === 3) {
          obj.experience = parts[2];
        }

        skillMap.set(key, obj);
      }
    });

    clonedHit.TKSkills = Array.from(skillMap.values());
  }

  return clonedHit;
};

const ResultsContainer = ({ children, searchResults, reRankHitsForJob }) => {
  const rankedHitsRef = useRef([]);
  const hitsRef = useRef([]);

  const {
    hits: stateHits,
    setHits,
    setDisjunctiveFacets,
    handleReadyForSearch,
    hideSkipped,
    hideRejected,
    hideMatchedAndApplied,
    pageIsLoaded,
    searchState,
  } = useContext(SearchContext);

  const { matches } = useContext(MatchContext);

  useEffect(() => {
    if (!searchResults) {
      return;
    }

    const hits = (searchResults?.hits || []).map((hit) =>
      buildTKSkillsBasedOnHierarchy(hit)
    );

    if (reRankHitsForJob?.id) {
      rankedHitsRef.current = hits;
    } else {
      hitsRef.current = hits;
    }

    setHits(hits);
    setDisjunctiveFacets(searchResults?.disjunctiveFacets || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults?.params, searchResults?.index, searchResults?.hits]);

  // locally filter out hits based on match change
  useEffect(() => {
    if (!pageIsLoaded || searchState.basicSearch) {
      return;
    }

    const filteredObjectIDs = getFilteredUserIDs(
      matches,
      hideSkipped,
      hideRejected,
      hideMatchedAndApplied
    );

    // If we switch between ra-rank and list tabs check and merge if user has been internally updated.
    if (reRankHitsForJob?.id) {
      rankedHitsRef.current = rankedHitsRef.current.filter(({ id }) => {
        return !filteredObjectIDs.includes(id);
      });

      setHits(rankedHitsRef.current);
    } else {
      hitsRef.current = hitsRef.current.filter(({ id }) => {
        return !filteredObjectIDs.includes(id);
      });

      setHits(hitsRef.current);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  useEffect(() => {
    if (searchState.basicSearch) {
      return;
    }

    const filteredObjectIDs = getFilteredUserIDs(
      matches,
      hideSkipped,
      hideRejected,
      hideMatchedAndApplied
    );

    const reRankHitsIds = rankedHitsRef.current.map(({ id }) => id);

    const hitsIds = hitsRef.current.map(({ id }) => id);

    if (!isEqual(reRankHitsIds.sort(), hitsIds.sort())) {
      handleReadyForSearch(true);
      return;
    }

    // If we switch between ra-rank and list tabs check and merge if user has been internally updated.
    if (reRankHitsForJob?.id) {
      const merged = mergeHitsIfChangedById(rankedHitsRef.current, stateHits);

      rankedHitsRef.current = merged.filter(({ id }) => {
        return !filteredObjectIDs.includes(id);
      });

      setHits(rankedHitsRef.current);
    } else {
      const merged = mergeHitsIfChangedById(hitsRef.current, stateHits);

      hitsRef.current = merged.filter(({ id }) => {
        return !filteredObjectIDs.includes(id);
      });

      setHits(hitsRef.current);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reRankHitsForJob?.id]);

  return <>{children}</>;
};

export default connectStateResults(ResultsContainer);
